import { TextField } from '@mui/material';
import { Field, FieldProps, useFormikContext } from 'formik';
import { camelCase, get } from 'lodash';
import React, { useCallback, useMemo } from 'react';

import type { InputComponent } from '..';

export interface InputTextAreaProps extends InputComponent {
  rows?: number;
}

export function InputTextArea({
  className,
  disabled,
  hidden,
  label,
  hint,
  nameOverride,
  rows = 3,
  required,
  readOnly = false,
  showLabel = !hidden,
  placeholder = '',
}: InputTextAreaProps) {
  const { errors } = useFormikContext<Record<string, string>>();

  const name = useMemo(
    () => nameOverride ?? camelCase(label),
    [label, nameOverride],
  );

  const error = useMemo(() => get(errors, name), [errors, name]);

  const validate = useCallback(
    (value: string | number | undefined) => {
      if (required && (!value || value === undefined)) {
        return `${label} is required.`;
      }
      return undefined;
    },
    [label, required],
  );

  const inputPlaceholder = useMemo(
    () => placeholder || label?.replace(/[?:]/g, '') || '',
    [placeholder, label],
  );

  return (
    <Field name={name} validate={validate} isInvalid={!!error}>
      {({ field }: FieldProps) => (
        <TextField
          multiline
          fullWidth
          variant="outlined"
          rows={rows}
          label={showLabel && label}
          className={className}
          disabled={disabled}
          hidden={hidden}
          required={required && !readOnly}
          placeholder={inputPlaceholder}
          slotProps={{
            inputLabel: {
              shrink: !!(showLabel && label),
            },
            input: {
              inputProps: {
                readOnly,
              } as React.HTMLAttributes<HTMLTextAreaElement>,
            },
          }}
          error={!!error}
          helperText={error ? error : hint}
          {...field}
          value={field.value || ''}
        />
      )}
    </Field>
  );
}
